import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AccountSnapshot, WelcomeAccountPayload } from './types.js';
import { AccountController } from './account-controller.js';
import { createAccountApi } from './api.js';

const AccountContext = createContext<AccountController | null>(null);

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const controller = useMemo(() => new AccountController(createAccountApi()), []);
  return <AccountContext.Provider value={controller}>{children}</AccountContext.Provider>;
}

export function useAccountController(): AccountController {
  const controller = useContext(AccountContext);
  if (!controller) throw new Error('AccountProvider missing');
  return controller;
}

export function useAccountSnapshot(): AccountSnapshot {
  const controller = useAccountController();
  const [snapshot, setSnapshot] = useState<AccountSnapshot>(controller.getSnapshot());

  useEffect(() => controller.subscribe(setSnapshot), [controller]);

  return snapshot;
}

export function useHydrateWelcomeAccount(): (payload: WelcomeAccountPayload) => void {
  const controller = useAccountController();
  // useCallback mantiene la referencia estable para no re-registrar el manejador
  // del Welcome en cada render (evita bucles de peticiones).
  return useCallback(
    (payload: WelcomeAccountPayload) => controller.hydrateWelcome(payload),
    [controller],
  );
}
